import AppLayout from '@/layouts/AppLayout'
import graphqlClient from '@/lib/graphql-client'
import LayoutService from '@/services/LayoutService'
import PageService from '@/services/PageService'
import getSectionData from '@/utils/getSectionData'
import dynamic from 'next/dynamic'

const DEFAULT_TEMPLATE = 'default'

function Page({ page, content, layoutData }) {
  const pageTemplate = page.pageTemplate || DEFAULT_TEMPLATE
  const Template = dynamic(() => import('@/templates/' + pageTemplate))

  return (
    <AppLayout
      page={page}
      data={layoutData}
    >
      <Template
        page={page}
        content={content}
      />
    </AppLayout>
  )
}

export async function getStaticPaths() {
  let defaultTemplatePages = []
  const BASE_URL = `${process.env.NEXT_PUBLIC_CMS_REST_ENDPOINT}/wp/v2/pages?per_page=100&_fields=slug,template`
  const { headers } = await fetch(BASE_URL)
  const pages = headers.get('x-wp-totalpages')

  for (let page = 1; page <= pages; page++) {
    const response = await fetch(`${BASE_URL}&page=${page}`)

    defaultTemplatePages = [...defaultTemplatePages, ...(await response.json())]
  }

  defaultTemplatePages = defaultTemplatePages.filter(
    (page) =>
      page.template !== 'index' &&
      page.slug !== 'lid-worden' &&
      page.slug !== 'bk-panel' &&
      page.slug !== 'ledenzoeker' &&
      page.slug !== 'persberichten' &&
      page.slug !== 'onderwerpen' &&
      page.slug !== 'podcasts' &&
      page.slug !== 'agenda' &&
      page.slug !== 'webinars' &&
      page.slug !== 'nieuws'
  )

  return {
    paths: defaultTemplatePages.map((page) => ({ params: { id: page.slug } })),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, preview, previewData }) {
  const { id } = params
  const isId = Number.isInteger(Number(id))
  const idType = isId ? 'DATABASE_ID' : 'URI'

  try {
    const { data: pageData } = await PageService().show(id, idType, previewData)
    const { data: layoutData } = await LayoutService().index()
    let page = pageData.page
    if (!page) return { notFound: true }

    if (preview) {
      page = { ...page, ...page.revisions.nodes[0] }
    }

    const pageTemplate = page.pageTemplate || DEFAULT_TEMPLATE

    const { default: GQL_QUERY } = await import('@/graphql/templates/' + pageTemplate + '.graphql')
    if (!GQL_QUERY) return { notFound: true }

    const { data: contentData } = await graphqlClient(GQL_QUERY, { id, idType }, null, previewData)
    const sectionData = getSectionData(contentData.content.sections)
    let content = { ...contentData.content }

    if (contentData.theme) {
      const socialsData = contentData.theme.settings.socials
      const socials = Object.entries(socialsData).map(([key, value]) => ({ name: key, url: value }))
      content = { ...contentData.content, socials }
    }

    if (preview) {
      content = { ...content, ...content.revisions.nodes[0] }
    }

    content.sections = sectionData

    return {
      props: { page, content, layoutData },
      revalidate: 10,
    }
  } catch (error) {
    throw new Error(error)
  }
}

export default Page
